import React, { useState } from 'react';
import axios from 'axios';
import { toast } from '../../lib/toast';
import OtpBoxInput from './OtpBoxInput';

const API = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const EmailVerificationPrompt = ({ user, onVerified }) => {
  const [dismissed, setDismissed] = useState(sessionStorage.getItem(`email_verify_dismissed_${user?.id}`) === '1');
  const [showModal, setShowModal] = useState(false);
  const [email, setEmail] = useState(user?.email || '');
  const [step, setStep] = useState('email'); // 'email' | 'code'
  const [challengeId, setChallengeId] = useState('');
  const [maskedEmail, setMaskedEmail] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  if (!user || user.email_verified || dismissed) return null;

  const token = localStorage.getItem('token');
  const authHeaders = { Authorization: `Bearer ${token}` };

  const dismiss = () => {
    sessionStorage.setItem(`email_verify_dismissed_${user.id}`, '1');
    setDismissed(true);
  };

  const requestCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const res = await axios.post(`${API}/api/auth/email/request-verification`, { email }, { headers: authHeaders });
      setChallengeId(res.data.challenge_id);
      setMaskedEmail(res.data.masked_email);
      setStep('code');
    } catch (err) {
      setError(err.response?.data?.detail || 'تعذّر إرسال رمز التحقق');
    } finally {
      setLoading(false);
    }
  };

  const confirmCode = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      await axios.post(`${API}/api/auth/email/confirm-verification`, { challenge_id: challengeId, code }, { headers: authHeaders });
      const updatedUser = { ...user, email, email_verified: true };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      toast.success('تم توثيق بريدك الإلكتروني بنجاح');
      setShowModal(false);
      if (onVerified) onVerified(updatedUser);
    } catch (err) {
      setError(err.response?.data?.detail || 'رمز غير صحيح');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        dir="rtl"
        data-testid="email-verification-banner"
        className="fixed bottom-[168px] left-6 z-40 flex max-w-[280px] items-start gap-2.5 rounded-2xl border border-amber-200 bg-white p-3.5 shadow-xl lg:bottom-24"
      >
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-4 w-4">
            <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0-.414.336-.75.75-.75h18a.75.75 0 01.75.75v10.5a.75.75 0 01-.75.75H3a.75.75 0 01-.75-.75V6.75zm.75-.75l9 6 9-6" />
          </svg>
        </span>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold leading-snug text-slate-700">
            وثّق بريدك الإلكتروني لتفعيل حماية إضافية (رمز دخول لمرة واحدة) على حسابك.
          </p>
          <div className="mt-2 flex items-center gap-3">
            <button
              type="button"
              data-testid="email-verification-open-button"
              onClick={() => setShowModal(true)}
              className="rounded-lg bg-amber-600 px-3 py-1.5 text-xs font-bold text-white hover:bg-amber-700 transition-colors"
            >
              توثيق البريد
            </button>
            <button
              type="button"
              data-testid="email-verification-dismiss-button"
              onClick={dismiss}
              className="text-xs font-medium text-slate-400 hover:text-slate-600"
            >
              إغلاق
            </button>
          </div>
        </div>
      </div>

      {showModal && (
        <div
          dir="rtl"
          className="fixed inset-0 z-[999] flex items-center justify-center bg-black/50 p-4"
          onClick={() => setShowModal(false)}
        >
          <div
            className="w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
            data-testid="email-verification-modal"
          >
            <h3 className="text-lg font-bold text-[#0A192F] mb-1">توثيق البريد الإلكتروني</h3>
            <p className="text-sm text-slate-500 mb-5">
              {step === 'email'
                ? 'أضف بريدك الإلكتروني لتفعيل تسجيل الدخول الآمن (OTP).'
                : `أدخل الرمز المرسل إلى ${maskedEmail}`}
            </p>

            {error && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-600" data-testid="email-verification-error">
                {error}
              </div>
            )}

            {step === 'email' ? (
              <form onSubmit={requestCode} className="space-y-4">
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="example@email.com"
                  data-testid="email-verification-email-input"
                  className="h-12 w-full rounded-xl bg-slate-50 px-4 text-slate-800 outline-none border border-slate-200 focus:ring-2 focus:ring-teal-600"
                  dir="ltr"
                />
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="email-verification-send-code-button"
                  className="h-12 w-full rounded-xl bg-teal-600 text-sm font-bold text-white disabled:opacity-50"
                >
                  {loading ? 'جارٍ الإرسال...' : 'إرسال رمز التحقق'}
                </button>
              </form>
            ) : (
              <form onSubmit={confirmCode} className="space-y-4">
                <OtpBoxInput length={6} value={code} onChange={setCode} autoFocus testIdPrefix="email-verify-code" />
                <button
                  type="submit"
                  disabled={loading || code.length !== 6}
                  data-testid="email-verification-confirm-button"
                  className="h-12 w-full rounded-xl bg-teal-600 text-sm font-bold text-white disabled:opacity-50"
                >
                  {loading ? 'جارٍ التحقق...' : 'تأكيد التوثيق'}
                </button>
              </form>
            )}

            <button
              type="button"
              onClick={() => setShowModal(false)}
              data-testid="email-verification-close-modal-button"
              className="mt-4 w-full text-center text-xs font-semibold text-slate-400 hover:text-slate-600"
            >
              إغلاق
            </button>
          </div>
        </div>
      )}
    </>
  );
};
